import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Icon from '../../../components/Icon'
import { FormGroup, Input, Select, TableShell } from '../../../components/ui'
import {
  getBudgetCardPath,
  getDepartmentObjectStageKey,
  getSharedBudgetCardPath,
  groupItemsByObjectAndType,
  pluralizeStageBudgets,
} from '../stageTableGrouping'
import { buildStageOverviewRows, formatDateRuShort, pluralizeRu } from './departmentDetailUtils'

function getStageStatusTone (status) {
  const value = String(status || '').toLowerCase()
  if (value.includes('просроч')) return 'ds-status-pill-overdue'
  if (value.includes('сдан') || value.includes('заверш') || value.includes('закры')) return 'ds-status-pill-done'
  if (value.includes('нов')) return 'ds-status-pill-draft'
  return 'ds-status-pill-active'
}

function getStageTypeOptionLabel (option) {
  if (typeof option === 'string') return option.trim()
  return String(option?.name || option?.label || option?.code || option?.id || '').trim()
}

function DepartmentStagesPanel ({
  activeTeam,
  plannedTeam,
  actualTeam,
  overviewRows: overviewRowsProp,
  stageTypeOptions = [],
  formatMoney,
}) {
  const navigate = useNavigate()
  const [typeFilter, setTypeFilter] = useState('all')
  const [yearFilter, setYearFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [collapsedObjects, setCollapsedObjects] = useState(() => new Set())
  const [expandedTypeGroups, setExpandedTypeGroups] = useState(() => new Set())

  const overviewRows = useMemo(
    () => overviewRowsProp || buildStageOverviewRows(plannedTeam, actualTeam),
    [actualTeam, overviewRowsProp, plannedTeam],
  )
  const years = useMemo(() => {
    const values = new Set()
    overviewRows.forEach((row) => {
      if (row.dueDate) values.add(String(new Date(row.dueDate).getFullYear()))
    })
    return [...values].sort()
  }, [overviewRows])
  const typeOptions = useMemo(() => {
    const seen = new Set()
    const fromRows = overviewRows
      .map((row) => String(row.objectStageName || '').trim())
      .filter(Boolean)
    const fromConfig = (stageTypeOptions || []).map(getStageTypeOptionLabel).filter(Boolean)
    return [...fromRows, ...fromConfig].reduce((options, label) => {
      const key = label.toLowerCase()
      if (!label || seen.has(key)) return options
      seen.add(key)
      options.push(label)
      return options
    }, [])
  }, [overviewRows, stageTypeOptions])

  const rows = useMemo(() => {
    const searchText = search.trim().toLowerCase()
    return overviewRows.filter((row) => {
      if (typeFilter !== 'all') {
        const rowType = String(row.objectStageName || row.stageType || row.stageName || '').toLowerCase()
        if (!rowType.includes(typeFilter.toLowerCase())) return false
      }
      if (statusFilter !== 'all' && !String(row.status || '').toLowerCase().includes(statusFilter.toLowerCase())) return false
      if (yearFilter !== 'all' && (!row.dueDate || String(new Date(row.dueDate).getFullYear()) !== yearFilter)) return false
      if (searchText && !`${row.objectName || ''} ${row.projectName || ''} ${row.contractNumber || ''} ${row.stageName || ''} ${row.objectStageName || ''}`.toLowerCase().includes(searchText)) return false
      return true
    })
  }, [overviewRows, search, statusFilter, typeFilter, yearFilter])

  const objectGroups = useMemo(() => {
    const groups = new Map()
    rows.forEach((row) => {
      const objectName = row.objectName || 'Без объекта'
      const key = row.objectId ? `object:${row.objectId}` : `object-name:${objectName}`
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          objectId: row.objectId,
          objectName,
          stages: [],
        })
      }
      groups.get(key).stages.push(row)
    })
    return [...groups.values()].map((group) => ({
      ...group,
      typeGroups: groupItemsByObjectAndType(
        group.stages,
        () => group.key,
        getDepartmentObjectStageKey,
      )[0]?.types || [],
      itemCount: group.stages.length,
    }))
  }, [rows])

  const toggleObjectGroup = (groupKey) => {
    setCollapsedObjects((current) => {
      const next = new Set(current)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  const toggleTypeGroup = (typeGroupKey) => {
    setExpandedTypeGroups((current) => {
      const next = new Set(current)
      if (next.has(typeGroupKey)) next.delete(typeGroupKey)
      else next.add(typeGroupKey)
      return next
    })
  }

  const openObject = (objectId) => {
    if (objectId) navigate(`/object/${objectId}`)
  }

  const openBudgetCard = (item) => {
    const path = getBudgetCardPath(item)
    if (path) navigate(path)
  }

  return (
    <section className="department-detail-section department-stages-panel" aria-labelledby="department-stages-heading">
      <div className="department-detail-section-head">
        <h2 id="department-stages-heading">Стадии команды {activeTeam?.name || ''}</h2>
        <span>{rows.length} {pluralizeRu(rows.length, 'стадия', 'стадии', 'стадий')}</span>
      </div>

      <div className="department-stages-filterbar">
        <FormGroup label="Этап объекта" className="department-stage-filter">
          <Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Этап объекта">
            <option value="all">Все</option>
            {typeOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Год срока" className="department-stage-filter">
          <Select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)} aria-label="Год срока">
            <option value="all">Все</option>
            {years.map((year) => <option key={year} value={year}>{year}</option>)}
          </Select>
        </FormGroup>
        <FormGroup label="Статус" className="department-stage-filter">
          <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Статус стадии">
            <option value="all">Все</option>
            <option value="Новая">Новая</option>
            <option value="В работе">В работе</option>
            <option value="Работы завершены">Работы завершены</option>
            <option value="Закрыта">Закрыта</option>
          </Select>
        </FormGroup>
        <FormGroup label="Поиск" className="department-stage-filter department-stage-filter-grow">
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Объект, договор или № стадии"
            aria-label="Поиск по стадиям"
          />
        </FormGroup>
      </div>

      {rows.length ? (
        <>
          <TableShell className="department-detail-table department-stages-table-shell">
            <table className="ds-data-table department-detail-data-table department-stages-table">
              <colgroup>
                <col className="department-stage-col-name" />
                <col className="department-stage-col-income" />
                <col className="department-stage-col-progress" />
                <col className="department-stage-col-earned" />
                <col className="department-stage-col-remaining" />
                <col className="department-stage-col-due" />
                <col className="department-stage-col-status" />
              </colgroup>
              <thead>
                <tr>
                  <th>№ стадии</th>
                  <th className="ds-align-right">Доход отдела (бюджет)</th>
                  <th className="ds-align-right">% работы</th>
                  <th className="ds-align-right">Заактировано</th>
                  <th className="ds-align-right">Ещё получит</th>
                  <th>Срок</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {objectGroups.map((group) => {
                  const isCollapsed = collapsedObjects.has(group.key)
                  return (
                  <React.Fragment key={group.key}>
                    <tr className="department-stage-group-row">
                      <td colSpan={7}>
                        <div className="department-stage-group-cell">
                          <button
                            type="button"
                            className="department-stage-group-toggle"
                            aria-expanded={!isCollapsed}
                            aria-label={`${isCollapsed ? 'Развернуть' : 'Свернуть'} объект ${group.objectName}`}
                            onClick={() => toggleObjectGroup(group.key)}
                          >
                            <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} size={14} />
                          </button>
                          {group.objectId ? (
                            <button
                              type="button"
                              className="department-stage-object-link"
                              onClick={() => openObject(group.objectId)}
                            >
                              {group.objectName}
                            </button>
                          ) : (
                            <strong>{group.objectName}</strong>
                          )}
                          <span className="department-stage-group-count">{group.itemCount}</span>
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && group.typeGroups.map((typeGroup) => {
                      const typeGroupKey = `${group.key}::${typeGroup.typeKey}`
                      const isTypeExpanded = expandedTypeGroups.has(typeGroupKey)
                      const shareTotal = typeGroup.items.reduce((sum, row) => sum + (row.plan || row.fact || 0), 0)
                      const actedTotal = typeGroup.items.reduce((sum, row) => sum + (row.fact || 0), 0)
                      const remainingTotal = typeGroup.items.reduce((sum, row) => sum + (row.remaining || 0), 0)
                      const budgetTotal = typeGroup.items.reduce((sum, row) => sum + (row.budget || 0), 0)
                      const completionPercent = shareTotal > 0 ? Math.min(100, Math.round((actedTotal / shareTotal) * 100)) : 0
                      const representative = typeGroup.items.reduce((best, row) => {
                        const bestRank = getStageStatusTone(best?.status || '').includes('overdue') ? 3 : 0
                        const rowRank = getStageStatusTone(row.status || '').includes('overdue') ? 3 : 0
                        return rowRank > bestRank ? row : best
                      }, typeGroup.items[0])
                      const budgetSub = typeGroup.items.length > 1
                        ? `${typeGroup.items.length} ${pluralizeStageBudgets(typeGroup.items.length)}`
                        : (budgetTotal > 0 ? `бюджет стадии ${formatMoney(budgetTotal)}` : '')
                      const groupBudgetPath = getSharedBudgetCardPath(typeGroup.items)
                      const typeGroupLabel = typeGroup.items.length === 1
                        ? (typeGroup.items[0].stageName || typeGroup.typeKey)
                        : typeGroup.typeKey
                      const singleStagePath = typeGroup.items.length === 1 && typeGroup.items[0].id
                        ? `/stage/${encodeURIComponent(typeGroup.items[0].id)}`
                        : null

                      return (
                        <React.Fragment key={typeGroupKey}>
                          <tr
                            className={`department-stage-type-row ${isTypeExpanded ? 'is-open' : ''}`}
                            onClick={(event) => {
                              if (event.target.closest('a, button')) return
                              toggleTypeGroup(typeGroupKey)
                            }}
                          >
                            <td>
                              <div className="department-stage-type-inner">
                                <span className="department-stage-type-chevron" aria-hidden>
                              <Icon name="chevronRight" size={12} />
                            </span>
                                <button
                                  type="button"
                                  className="department-stage-type-name"
                                  onClick={() => {
                                    if (singleStagePath) navigate(singleStagePath)
                                    else toggleTypeGroup(typeGroupKey)
                                  }}
                                >
                                  {typeGroupLabel}
                                </button>
                                <span className="department-stage-type-count">{typeGroup.items.length}</span>
                                {groupBudgetPath ? (
                                  <Link
                                    to={groupBudgetPath}
                                    className="department-stage-type-detail"
                                    aria-label={`Открыть этап ${typeGroup.typeKey}`}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <Icon name="chevronRight" size={14} />
                                  </Link>
                                ) : null}
                              </div>
                            </td>
                            <td className="ds-mono department-stage-amount">
                              {formatMoney(shareTotal)}
                              {budgetSub ? <small className="department-stage-budget-sub">{budgetSub}</small> : null}
                            </td>
                            <td className="ds-mono">
                              <span className="department-stage-progress">
                                <span className="department-stage-progress-track">
                                  <span style={{ width: `${completionPercent}%` }} />
                                </span>
                                {completionPercent}%
                              </span>
                            </td>
                            <td className="ds-mono department-stage-amount department-stage-amount-earned">{formatMoney(actedTotal)}</td>
                            <td className="ds-mono department-stage-amount">{formatMoney(remainingTotal)}</td>
                            <td>{representative?.dueDate ? formatDateRuShort(representative.dueDate) : '—'}</td>
                            <td>
                              <span className={`ds-status-pill ${getStageStatusTone(representative?.status)}`}>
                                {representative?.status}
                              </span>
                            </td>
                          </tr>
                          {isTypeExpanded && typeGroup.items.map((row) => {
                            const budgetPath = getBudgetCardPath(row)
                            return (
                            <tr key={row.id} className="department-stage-detail-row">
                              <td>
                                {budgetPath ? (
                                <button
                                  type="button"
                                  className="department-stage-link"
                                  onClick={() => openBudgetCard(row)}
                                >
                                  {row.stageName}
                                </button>
                                ) : (
                                  <span>{row.stageName}</span>
                                )}
                              </td>
                              <td className="ds-mono department-stage-amount">
                                {formatMoney(row.plan || row.fact)}
                                {row.budget > 0 && <small className="department-stage-budget-sub">бюджет стадии {formatMoney(row.budget)}</small>}
                              </td>
                              <td className="ds-mono">
                                <span className="department-stage-progress">
                                  <span className="department-stage-progress-track">
                                    <span style={{ width: `${row.completionPercent}%` }} />
                                  </span>
                                  {row.completionPercent}%
                                </span>
                              </td>
                              <td className="ds-mono department-stage-amount department-stage-amount-earned">{formatMoney(row.fact)}</td>
                              <td className="ds-mono department-stage-amount">{formatMoney(row.remaining)}</td>
                              <td>{row.dueDate ? formatDateRuShort(row.dueDate) : '—'}</td>
                              <td>
                                <span className={`ds-status-pill ${getStageStatusTone(row.status)}`}>
                                  {row.status}
                                </span>
                              </td>
                            </tr>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </TableShell>

          <div className="department-stages-mobile-list" aria-label="Стадии команды списком">
            {objectGroups.map((group) => {
              const isCollapsed = collapsedObjects.has(group.key)
              return (
              <section key={group.key} className="department-stage-mobile-group" aria-label={group.objectName}>
                <div className="department-stage-mobile-group-head">
                  <button
                    type="button"
                    className="department-stage-group-toggle"
                    aria-expanded={!isCollapsed}
                    aria-label={`${isCollapsed ? 'Развернуть' : 'Свернуть'} объект ${group.objectName}`}
                    onClick={() => toggleObjectGroup(group.key)}
                  >
                    <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} size={14} />
                  </button>
                  {group.objectId ? (
                    <button
                      type="button"
                      className="department-stage-object-link"
                      onClick={() => openObject(group.objectId)}
                    >
                      {group.objectName}
                    </button>
                  ) : (
                    <strong>{group.objectName}</strong>
                  )}
                  <span>{group.itemCount} {pluralizeRu(group.itemCount, 'стадия', 'стадии', 'стадий')}</span>
                </div>
                {!isCollapsed && group.typeGroups.map((typeGroup) => {
                  const typeGroupKey = `${group.key}::${typeGroup.typeKey}`
                  const isTypeExpanded = expandedTypeGroups.has(typeGroupKey)
                  return (
                    <React.Fragment key={typeGroupKey}>
                      <button
                        type="button"
                        className={`department-stage-mobile-type-head ${isTypeExpanded ? 'is-open' : ''}`}
                        onClick={() => toggleTypeGroup(typeGroupKey)}
                      >
                        <span className="department-stage-type-chevron" aria-hidden>
                          <Icon name="chevronRight" size={12} />
                        </span>
                        <span className="department-stage-type-name">{typeGroup.typeKey}</span>
                        <span>{typeGroup.items.length}</span>
                      </button>
                      {isTypeExpanded && typeGroup.items.map((row) => {
                        const budgetPath = getBudgetCardPath(row)
                        return (
                  <article key={row.id} className="department-stage-mobile-card">
                    <div className="department-stage-mobile-title">
                      {budgetPath ? (
                      <button
                        type="button"
                        className="department-stage-link"
                        onClick={() => openBudgetCard(row)}
                      >
                        {row.stageName}
                      </button>
                      ) : (
                        <span>{row.stageName}</span>
                      )}
                      <span className={`ds-status-pill ${getStageStatusTone(row.status)}`}>{row.status}</span>
                    </div>
                    <div className="department-stage-mobile-progress">
                      <span className="department-stage-progress-track">
                        <span style={{ width: `${row.completionPercent}%` }} />
                      </span>
                      <span>{row.completionPercent}%</span>
                    </div>
                    <dl className="department-stage-mobile-metrics">
                      <div>
                        <dt>Доход отдела</dt>
                        <dd>{formatMoney(row.plan || row.fact)}</dd>
                      </div>
                      <div>
                        <dt>Заактировано</dt>
                        <dd>{formatMoney(row.fact)}</dd>
                      </div>
                      <div>
                        <dt>Ещё получит</dt>
                        <dd>{formatMoney(row.remaining)}</dd>
                      </div>
                      <div>
                        <dt>Срок</dt>
                        <dd>{row.dueDate ? formatDateRuShort(row.dueDate) : '—'}</dd>
                      </div>
                    </dl>
                    {row.budget > 0 && (
                      <p className="department-stage-mobile-budget">Бюджет стадии {formatMoney(row.budget)}</p>
                    )}
                  </article>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </section>
              )
            })}
          </div>
        </>
      ) : (
        <div className="department-detail-empty-state">
          Нет стадий для выбранной команды
        </div>
      )}
    </section>
  )
}

export default DepartmentStagesPanel
